/**
 * Centralized KaTeX Rendering Library for BayesPrep
 *
 * This library provides consistent LaTeX/KaTeX rendering across all portals:
 * - Admin Portal
 * - Student Portal
 * - Super Admin Portal
 *
 * Features:
 * - Inline math ($...$) and block math ($$...$$) support
 * - Robust error handling with fallback rendering
 * - Table-optimized rendering with truncation
 * - Preview components for forms
 * - Consistent styling and behavior
 */

import React from 'react';

// Import KaTeX CSS
import 'katex/dist/katex.min.css';

// Import react-katex components
import { InlineMath, BlockMath } from 'react-katex';

/**
 * Clean math text for fallback display
 */
const cleanMathText = (mathText) => {
  if (!mathText) return '';

  return mathText
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1)/($2)')
    .replace(/\\sqrt\{([^}]+)\}/g, '√($1)')
    .replace(/\\text\{([^}]+)\}/g, '$1')
    .replace(/\{([^}]+)\}/g, '$1')
    .replace(/\\([a-zA-Z]+)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Core LaTeX rendering function
 * Supports both inline ($...$) and block ($$...$$) LaTeX
 * Uses react-katex if available, falls back to cleaned text otherwise
 */
export const renderLatex = (text) => {
  if (!text) return text;

  try {
    // Check if we have LaTeX content
    if (!hasLatex(text)) {
      return <span className="text-content">{text}</span>;
    }

    // Handle block math ($$...$$) first
    const blockMathRegex = /\$\$(.*?)\$\$/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = blockMathRegex.exec(text)) !== null) {
      // Add text before the math
      if (match.index > lastIndex) {
        const beforeText = text.slice(lastIndex, match.index);
        parts.push(renderInlineMath(beforeText, parts.length));
      }

      // Add block math with KaTeX
      try {
        parts.push(
          <div key={parts.length} className="my-2">
            <BlockMath math={match[1]} />
          </div>
        );
      } catch (error) {
        console.warn('BlockMath rendering failed, using fallback:', error);
        parts.push(
          <div key={parts.length} className="my-2 katex-block-math" data-math={match[1]}>
            <div className="math-content bg-gray-50 p-2 rounded border text-center">
              {cleanMathText(match[1])}
            </div>
          </div>
        );
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      parts.push(renderInlineMath(remainingText, parts.length));
    }

    return parts.length > 1 ? <>{parts}</> : parts[0] || text;
  } catch (error) {
    console.warn('LaTeX rendering failed:', error);
    return <span>{text}</span>;
  }
};

/**
 * Renders inline math ($...$) within text
 */
const renderInlineMath = (text, keyOffset = 0) => {
  if (!text) return text;

  try {
    const inlineMathRegex = /\$(.*?)\$/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = inlineMathRegex.exec(text)) !== null) {
      // Add text before the math
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      // Add inline math with KaTeX
      try {
        parts.push(
          <InlineMath key={keyOffset + parts.length} math={match[1]} />
        );
      } catch (error) {
        console.warn('InlineMath rendering failed, using fallback:', error);
        parts.push(
          <span key={keyOffset + parts.length} className="katex-inline-math" data-math={match[1]}>
            {cleanMathText(match[1])}
          </span>
        );
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length > 1 ? <>{parts}</> : parts[0] || text;
  } catch (error) {
    console.warn('Inline math rendering failed:', error);
    return text;
  }
};

/**
 * Checks if text contains LaTeX delimiters
 */
export const hasLatex = (text) => {
  if (!text) return false;
  return /\$.*?\$|\$\$.*?\$\$/.test(text);
};

/**
 * Optimized LaTeX rendering for table display
 * Includes truncation and simplified rendering for performance
 */
export const renderLatexForTable = (text, maxLength = 80) => {
  if (!text) return <span className="text-gray-400">No text</span>;

  try {
    // For table display, first try to render with KaTeX if it contains math
    if (hasLatex(text)) {
      // For short math expressions, try to render them
      if (text.length < maxLength) {
        try {
          return renderLatex(text);
        } catch (error) {
          console.warn('KaTeX rendering failed in table, falling back to cleaned text:', error);
        }
      }
    }

    // Clean up LaTeX for display while preserving spaces
    let displayText = text;
    if (text.includes('\\')) {
      displayText = text
        .replace(/\\%/g, '%')
        .replace(/\\\$/g, '$')
        .replace(/\\&/g, '&')
        .replace(/\\#/g, '#')
        .replace(/\\_/g, '_')
        .replace(/\\\{/g, '{')
        .replace(/\\\}/g, '}')
        .replace(/\\text\{([^}]*)\}/g, '$1')
        .replace(/\\\\/g, ' ')
        .replace(/\$\$/g, '')
        .replace(/\$/g, '')
        .replace(/\\[a-zA-Z]+\{([^}]*)\}/g, ' $1 ')
        .replace(/\\[a-zA-Z]+/g, ' ')
        .replace(/[{}]/g, '')
        .replace(/([a-zA-Z])([A-Z])/g, '$1 $2')
        .replace(/([a-z])(\d)/g, '$1 $2')
        .replace(/(\d)([a-zA-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .replace(/\s*([.,;:!?])\s*/g, '$1 ')
        .trim();
    }

    // Truncate for table display
    const truncatedText = displayText.length > maxLength ? displayText.substring(0, maxLength) + '...' : displayText;

    return (
      <span
        title={text}
        className="cursor-help leading-relaxed"
        style={{ wordSpacing: '0.1em' }}
      >
        {truncatedText || 'Empty question'}
      </span>
    );
  } catch (error) {
    console.warn('LaTeX rendering error:', error);
    let fallbackText = text
      .replace(/\\%/g, '%')
      .replace(/\\\$/g, '$')
      .replace(/\\&/g, '&')
      .replace(/([a-zA-Z])([A-Z])/g, '$1 $2')
      .replace(/([a-z])(\d)/g, '$1 $2')
      .replace(/(\d)([a-zA-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();

    fallbackText = fallbackText.length > maxLength ? fallbackText.substring(0, maxLength) + '...' : fallbackText;
    return <span title={text} className="leading-relaxed" style={{ wordSpacing: '0.1em' }}>{fallbackText}</span>;
  }
};

/**
 * Simple preview component for LaTeX text
 */
export const LatexPreview = ({ 
  text, 
  className = '', 
  placeholder = 'Preview will appear here...' 
}) => {
  if (!text?.trim()) {
    return (
      <div className={`text-muted-foreground italic ${className}`}>
        {placeholder}
      </div>
    );
  }

  return (
    <div className={className}>
      {renderLatex(text)}
    </div>
  );
};

/**
 * Configuration object for different portal themes
 */
export const PORTAL_THEMES = {
  ADMIN: {
    primary: 'blue',
    accent: 'indigo',
    success: 'green',
    warning: 'yellow',
    error: 'red'
  },
  STUDENT: {
    primary: 'emerald',
    accent: 'teal',
    success: 'green',
    warning: 'amber',
    error: 'red'
  },
  SUPER_ADMIN: {
    primary: 'blue',
    accent: 'azure',
    success: 'green',
    warning: 'yellow',
    error: 'red'
  }
};

/**
 * Get theme-specific CSS classes
 */
export const getThemeClasses = (portal = 'ADMIN') => {
  const theme = PORTAL_THEMES[portal] || PORTAL_THEMES.ADMIN;
  
  return {
    primary: `text-${theme.primary}-600 bg-${theme.primary}-50 border-${theme.primary}-200`,
    accent: `text-${theme.accent}-600 bg-${theme.accent}-50 border-${theme.accent}-200`,
    success: `text-${theme.success}-600 bg-${theme.success}-50 border-${theme.success}-200`,
    warning: `text-${theme.warning}-600 bg-${theme.warning}-50 border-${theme.warning}-200`,
    error: `text-${theme.error}-600 bg-${theme.error}-50 border-${theme.error}-200`
  };
};
